import {useEffect, useState} from 'react'
import {useAuth} from '../modules/auth'
import {
  getConvertibleSecurityClasses,
  getShareholders,
  getShareSecurityClasses,
  getShareTransactions,
  getWarrantSecurityClasses,
} from '../modules/stakeholders/stakeholders-and-investors/core/_requests'
import {toast} from 'react-toastify'
import {useParams} from 'react-router-dom'
import {TransactionModalType} from '../modules/stakeholders/stakeholders-and-investors/core/_models'
import {TransactionModals} from '../modules/stakeholders/stakeholders-and-investors/core/_constants'

export const useGetAllSecurityClassesDropdown = (modalType: TransactionModalType) => {
  const [shareSecuirtyClassDropdownData, setShareSecurityClasssDropdownData] = useState<any[]>([])
  const [shareSecuirtyDropdownDataLoading, setShareSecuirtyDropdownDataLoading] =
    useState<boolean>(false)
  const [convertibleSecuirtyClassDropdownData, setConvertibleSecurityClasssDropdownData] = useState<
    any[]
  >([])
  const [convertibleSecuirtyDropdownDataLoading, setConvertibleSecuirtyDropdownDataLoading] =
    useState<boolean>(false)
  const [warrantSecuirtyClassDropdownData, setWarrantSecurityClasssDropdownData] = useState<any[]>(
    []
  )
  const [shareTransactionsDropdownData, setShareTransactionsDropdownData] = useState<any[]>([])
  const [shareTransactionDropdownLoading, setShareTransactionDropdownLoading] =
    useState<boolean>(false)
  const [warrantSecuirtyDropdownDataLoading, setWarrantSecuirtyDropdownDataLoading] =
    useState<boolean>(false)
  const [shareholdersDropdownData, setShareholdersDropdownData] = useState<any[]>([])
  const [shareholdersDropdownLoading, setShareholdersDropdownLoading] = useState<boolean>(false)
  const [shareSecuirtyClassDropdownDataForBuyback, setShareSecurityClasssDropdownDataForBuyback] =
    useState<any[]>([])
  const [
    shareSecuirtyDropdownDataForBuybackLoading,
    setShareSecuirtyDropdownDataForBuybackLoading,
  ] = useState(false)
  const {shareholderId} = useParams()

  const {companyId} = useAuth()

  const fetchShareSecurityClasses = async () => {
    if (!companyId) return
    setShareSecuirtyDropdownDataLoading(true)
    try {
      const {
        data: {data, success, errors},
      } = await getShareSecurityClasses(companyId, 1, 10, undefined, 'true')
      if (success) {
        setShareSecurityClasssDropdownData(
          data.shareSecurityClasses.map((shareSecurityClass: any) => ({
            id: shareSecurityClass.shareClassId,
            label: shareSecurityClass.className,
            value: shareSecurityClass.shareClassId,
            votePerShare: shareSecurityClass.votePerShare,
            pricePerShare: shareSecurityClass.pricePerShare,
            numberOfShares: shareSecurityClass.numberOfShares,
            firstClosingDate: shareSecurityClass.firstClosingDate,
          }))
        )
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.log(error)
    } finally {
      setShareSecuirtyDropdownDataLoading(false)
    }
  }

  const fetchShareSecurityClassesForBuyback = async () => {
    if (!companyId) return
    setShareSecuirtyDropdownDataForBuybackLoading(true)
    try {
      const {
        data: {data, success, errors},
      } = await getShareSecurityClasses(companyId, 1, 10, undefined, 'true', 'true')
      if (success) {
        setShareSecurityClasssDropdownDataForBuyback(
          data.shareSecurityClasses.map((shareSecurityClass: any) => ({
            id: shareSecurityClass.shareClassId,
            label: shareSecurityClass.className,
            value: shareSecurityClass.shareClassId,
            shareTransactions: shareSecurityClass.sharesTransactions,
            numberOfShares: shareSecurityClass.sharesTransactions.totalShareCountForTransactions,
            pricePerShare: shareSecurityClass.pricePerShare,
            shareholderName: shareSecurityClass.sharesTransactions.shareholderName,
            shareholderEmail: shareSecurityClass.sharesTransactions.shareholderEmail,
            shareholderProfileImage: shareSecurityClass.sharesTransactions.shareholderProfileImage,
            firstClosingDate: shareSecurityClass.firstClosingDate,
          }))
        )
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.log(error)
    } finally {
      setShareSecuirtyDropdownDataForBuybackLoading(false)
    }
  }

  const fetchConvertibleSecurityClasses = async () => {
    if (!companyId) return
    setConvertibleSecuirtyDropdownDataLoading(true)
    try {
      const {
        data: {data, success},
      } = await getConvertibleSecurityClasses(companyId, 1, 10, undefined, undefined, 'true')
      if (success) {
        setConvertibleSecurityClasssDropdownData(
          data.convertibleSecurityClasses.map((convertibleSecurityClass: any) => ({
            id: convertibleSecurityClass.convertibleClassId,
            label: convertibleSecurityClass.className,
            value: convertibleSecurityClass.convertibleClassId,
            votePerShare: convertibleSecurityClass.votePerShare,
            dateOfIssue: convertibleSecurityClass.dateOfIssue,
          }))
        )
      }
    } catch (error) {
      console.log(error)
    } finally {
      setConvertibleSecuirtyDropdownDataLoading(false)
    }
  }

  const fetchWarrantSecurityClasses = async () => {
    if (!companyId) return
    setWarrantSecuirtyDropdownDataLoading(true)
    try {
      const {
        data: {data, success},
      } = await getWarrantSecurityClasses(companyId, 1, 10, undefined, 'true')
      if (success) {
        setWarrantSecurityClasssDropdownData(
          data.warrantSecurityClasses.map((warrantSecurityClass: any) => ({
            id: warrantSecurityClass.warrantClassId,
            label: warrantSecurityClass.className,
            value: warrantSecurityClass.warrantClassId,
            votePerShare: warrantSecurityClass.votePerShare,
            expiryDate: warrantSecurityClass.expiryDate,
            boardApprovalDate: warrantSecurityClass.boardApprovalDate,
          }))
        )
      }
    } catch (error) {
      console.log(error)
    } finally {
      setWarrantSecuirtyDropdownDataLoading(false)
    }
  }

  const fetchShareTransactions = async () => {
    if (!companyId) return
    if (!shareholderId) return
    setShareTransactionDropdownLoading(true)
    try {
      const {
        data: {data, success},
      } = await getShareTransactions(shareholderId, companyId, 1, 10, undefined, undefined, 'true')
      if (success) {
        setShareTransactionsDropdownData(
          data.shareTransactions.map((shareTransaction: any) => ({
            id: shareTransaction.shareTransactionId,
            label: `${shareTransaction.shareClass.className} | ${shareTransaction.numberOfShares} shares`,
            value: shareTransaction.shareTransactionId,
            pricePerShare: shareTransaction.pricePerShare,
            numberOfShares: shareTransaction.numberOfShares,
            dateOfIssue: shareTransaction.issueDate,
          }))
        )
      }
    } catch (error) {
      console.log(error)
    } finally {
      setShareTransactionDropdownLoading(false)
    }
  }

  const fetchShareholders = async () => {
    if (!companyId) return
    setShareholdersDropdownLoading(true)
    try {
      const {
        data: {data, success},
      } = await getShareholders(companyId, 1, 10, undefined, undefined, 'true', shareholderId)
      if (success) {
        setShareholdersDropdownData(
          data.shareholders.map((shareholder: any) => ({
            id: shareholder.shareholderId,
            label: `${shareholder.firstName} ${shareholder.lastName}`,
            value: shareholder.shareholderId,
          }))
        )
      }
    } catch (error) {
      console.log(error)
    } finally {
      setShareholdersDropdownLoading(false)
    }
  }

  useEffect(() => {
    if (
      modalType === TransactionModals.ADD_CONVERTIBLE_CLASS_MODAL ||
      modalType === TransactionModals.ADD_SHARES_TRANSACTION ||
      modalType === TransactionModals.ADD_WARRANT_CLASS_MODAL
    ) {
      fetchShareSecurityClasses()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalType === TransactionModals.ADD_CONVERTIBLE_TRANSACTION) {
      fetchConvertibleSecurityClasses()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalType === TransactionModals.ADD_WARRANT_TRANSACTION) {
      fetchWarrantSecurityClasses()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalType === TransactionModals.ADD_TRANSFERRED_TRANSACTION) {
      fetchShareTransactions()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalType === TransactionModals.ADD_TRANSFERRED_TRANSACTION) {
      fetchShareholders()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalType === TransactionModals.ADD_BUYBACK_TRANSACTION) {
      fetchShareSecurityClassesForBuyback()
    }
  }, [modalType]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    shareSecuirtyClassDropdownData,
    setShareSecurityClasssDropdownData,
    fetchShareSecurityClasses,
    shareSecuirtyDropdownDataLoading,
    setShareSecuirtyDropdownDataLoading,
    convertibleSecuirtyClassDropdownData,
    setConvertibleSecurityClasssDropdownData,
    fetchConvertibleSecurityClasses,
    convertibleSecuirtyDropdownDataLoading,
    setConvertibleSecuirtyDropdownDataLoading,
    warrantSecuirtyClassDropdownData,
    setWarrantSecurityClasssDropdownData,
    fetchWarrantSecurityClasses,
    warrantSecuirtyDropdownDataLoading,
    setWarrantSecuirtyDropdownDataLoading,
    shareTransactionsDropdownData,
    setShareTransactionsDropdownData,
    fetchShareTransactions,
    shareTransactionDropdownLoading,
    setShareTransactionDropdownLoading,
    shareholdersDropdownData,
    setShareholdersDropdownData,
    fetchShareholders,
    shareholdersDropdownLoading,
    setShareholdersDropdownLoading,
    shareSecuirtyClassDropdownDataForBuyback,
    setShareSecurityClasssDropdownDataForBuyback,
    fetchShareSecurityClassesForBuyback,
    shareSecuirtyDropdownDataForBuybackLoading,
    setShareSecuirtyDropdownDataForBuybackLoading,
  }
}
